(() => {
  'use strict';

  const STORAGE_KEY = 'seremori:order-tracking';
  const INTENT_TTL = 15 * 60 * 1000;
  const REQUEST_TIMEOUT = 20000;

  function normalize(value) {
    return String(value == null ? '' : value).trim().replace(/^#/, '').trim().toLowerCase();
  }

  function validNumber(value) {
    return value.length <= 64 && normalize(value).length > 0 && !/[\u0000-\u001f\u007f]/.test(value);
  }

  function initialize(root) {
    if (root.dataset.trackingReady) return;
    const form = root.querySelector('[data-tracking-form]');
    const input = form && form.querySelector('[name="order_number"]');
    const button = form && form.querySelector('[type="submit"]');
    const status = root.querySelector('[data-tracking-status]');
    const result = root.querySelector('[data-tracking-result]');
    if (!form || !input || !button || !status || !result) return;
    root.dataset.trackingReady = 'true';

    const origin = window.location.origin;
    const pagePath = root.dataset.pagePath || '/pages/track-order';
    const sectionId = root.dataset.sectionId;
    const designMode = root.dataset.designMode === 'true' || Boolean(window.Shopify && window.Shopify.designMode);
    let authenticated = root.dataset.authenticated === 'true';
    let request = null;
    let generation = 0;

    const message = (key, fallback) => root.dataset[key] || fallback;

    function setBusy(busy) {
      button.disabled = busy;
      form.setAttribute('aria-busy', String(busy));
      button.setAttribute('aria-busy', String(busy));
    }

    function clearResult() {
      result.replaceChildren();
      result.hidden = true;
    }

    function cancel() {
      generation += 1;
      if (request) request.abort();
      request = null;
      setBusy(false);
    }

    function accountLink(value) {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, origin);
        if (!/^https?:$/.test(url.protocol) || url.username || url.password) return null;
        if (url.origin === origin) return url.href;
        const account = new URL(root.dataset.accountUrl, origin);
        const prefix = account.pathname.replace(/\/$/, '');
        if (url.protocol === 'https:' && url.origin === 'https://shopify.com' &&
            account.origin === url.origin && /^\/\d+\/account$/.test(prefix) &&
            (url.pathname === prefix || url.pathname.startsWith(prefix + '/'))) return url.href;
      } catch (_) { /* An invalid destination never becomes a link. */ }
      return null;
    }

    function loginLink() {
      if (!root.dataset.loginUrl) return null;
      try {
        const url = new URL(root.dataset.loginUrl, origin);
        if (url.origin === origin && /^https?:$/.test(url.protocol) && !url.username && !url.password) return url.href;
      } catch (_) { /* Keep the page usable if the platform route is missing. */ }
      return null;
    }

    function showVerification(text) {
      status.textContent = text;
      const href = loginLink();
      if (!href || designMode) return;
      const link = document.createElement('a');
      link.className = 'order-tracking__verify';
      link.href = href;
      link.textContent = message('verifyLabel', 'Verify purchase email');
      link.addEventListener('click', () => {
        if (validNumber(input.value)) saveIntent(input.value.trim());
      });
      result.replaceChildren(link);
      result.hidden = false;
    }

    function saveIntent(number) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ number, time: Date.now() }));
        return true;
      } catch (_) {
        return false;
      }
    }

    function consumeIntent() {
      try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        window.sessionStorage.removeItem(STORAGE_KEY);
        if (!raw) return null;
        const intent = JSON.parse(raw);
        const age = Date.now() - intent.time;
        if (typeof intent.number === 'string' && validNumber(intent.number) &&
            Number.isFinite(intent.time) && age >= 0 && age < INTENT_TTL) return intent.number;
      } catch (_) { /* Storage is optional; a visitor can re-enter the number. */ }
      return null;
    }

    function readData(scope) {
      const script = scope.querySelector('script[data-tracking-data]');
      if (!script) throw new Error('session');
      const data = JSON.parse(script.textContent);
      if (!data || !Array.isArray(data.orders) ||
          !(data.nextUrl == null || typeof data.nextUrl === 'string')) throw new Error('data');
      return data;
    }

    function pageUrl(value) {
      const url = new URL(value, origin + pagePath);
      if (url.origin !== origin || url.username || url.password ||
          url.pathname !== pagePath || !/\/pages\/track-order\/?$/.test(pagePath) ||
          !sectionId) throw new Error('pagination');
      url.hash = '';
      url.searchParams.set('section_id', sectionId);
      url.searchParams.sort();
      return url.href;
    }

    function firstPageUrl() {
      const url = new URL(pagePath, origin);
      const current = new URL(window.location.href);
      ['preview_theme_id', 'preview_key', '_fd', 'pb'].forEach(key => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      url.searchParams.set('page', '1');
      return pageUrl(url.href);
    }

    async function loadPage(href, signal) {
      const response = await fetch(href, { credentials: 'same-origin', cache: 'no-store', redirect: 'manual', signal });
      if (response.type === 'opaqueredirect' || response.status === 401 || response.status === 403) throw new Error('session');
      if (!response.ok) throw new Error('network');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = Array.from(doc.querySelectorAll('[data-order-tracking]'))
        .find(candidate => candidate.dataset.sectionId === sectionId);
      if (!section || section.dataset.authenticated !== 'true') throw new Error('session');
      return readData(section);
    }

    function renderOrder(order) {
      const heading = document.createElement('h2');
      heading.className = 'order-tracking__result-title';
      heading.textContent = String(order.name || order.number || '');
      heading.tabIndex = -1;
      const details = document.createElement('dl');
      details.className = 'order-tracking__details';
      [
        [message('dateLabel', 'Order date'), order.date],
        [message('paymentLabel', 'Payment'), order.payment],
        [message('fulfillmentLabel', 'Fulfillment'), order.fulfillment]
      ].forEach(([label, value]) => {
        if (value == null || value === '') return;
        const row = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = String(value);
        row.append(term, description);
        details.append(row);
      });
      result.replaceChildren(heading, details);
      if (order.cancelled === true) {
        const cancelled = document.createElement('p');
        cancelled.className = 'order-tracking__cancelled';
        cancelled.textContent = message('cancelledLabel', 'This order was cancelled.');
        result.append(cancelled);
      }
      const href = accountLink(order.url) || accountLink(root.dataset.accountUrl);
      if (href) {
        const link = document.createElement('a');
        link.className = 'button button--primary order-tracking__details-link';
        link.href = href;
        link.textContent = message('viewOrderLabel', 'View order details');
        result.append(link);
      }
      result.hidden = false;
      status.textContent = message('foundMessage', 'Your order is ready to view.');
      heading.focus({ preventScroll: true });
    }

    async function search(number) {
      cancel();
      clearResult();
      input.removeAttribute('aria-invalid');
      if (!validNumber(number)) {
        status.textContent = message('invalidMessage', 'Enter the full order number from your confirmation email.');
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      number = number.trim();
      if (designMode) {
        status.textContent = message('designMessage', 'Open the storefront to look up an order.');
        return;
      }
      if (!authenticated) {
        const href = loginLink();
        if (!href) {
          status.textContent = message('errorMessage', 'We could not load your order. Please try again.');
          return;
        }
        if (!saveIntent(number)) {
          showVerification(message('sessionMessage', 'Verify your purchase email, then return here and enter your order number again.'));
          return;
        }
        setBusy(true);
        status.textContent = message('verifyMessage', 'Continue to verify the email used at checkout.');
        window.location.assign(href);
        return;
      }

      const current = generation;
      const controller = new AbortController();
      request = controller;
      const target = normalize(number);
      const visited = new Set();
      setBusy(true);
      status.textContent = message('loadingMessage', 'Finding your order…');
      async function fetchPage(href) {
        if (visited.has(href)) throw new Error('pagination');
        visited.add(href);
        const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
        try {
          return await loadPage(href, controller.signal);
        } finally {
          window.clearTimeout(timeout);
        }
      }
      try {
        // Re-check the server session for every search, including the first page.
        // Another tab may have signed out or changed the verified customer.
        let data = await fetchPage(firstPageUrl());
        while (true) {
          if (current !== generation) return;
          const order = data.orders.find(item => item &&
            (normalize(item.name) === target || normalize(item.number) === target));
          if (order) {
            renderOrder(order);
            return;
          }
          if (!data.nextUrl) {
            status.textContent = message('noMatchMessage', 'We could not find that order for the email you verified. Check the full number in your confirmation email.');
            return;
          }
          const href = pageUrl(data.nextUrl);
          data = await fetchPage(href);
        }
      } catch (error) {
        if (current !== generation) return;
        if (error.message === 'session') {
          authenticated = false;
          showVerification(message('verifyMessage', 'Please verify your purchase email again to view this order.'));
        } else {
          status.textContent = message('errorMessage', 'We could not load your order. Please try again.');
        }
      } finally {
        if (current === generation) {
          request = null;
          setBusy(false);
        }
      }
    }

    form.addEventListener('submit', event => {
      event.preventDefault();
      search(input.value);
    });
    input.addEventListener('input', () => {
      cancel();
      clearResult();
      input.removeAttribute('aria-invalid');
      status.textContent = '';
    });
    window.addEventListener('pagehide', () => {
      cancel();
      clearResult();
      input.value = '';
      status.textContent = '';
      root.querySelectorAll('script[data-tracking-data]').forEach(script => { script.textContent = ''; });
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted) window.location.reload();
    });
    form.hidden = false;
    if (!designMode) {
      const pending = consumeIntent();
      if (pending) {
        input.value = pending;
        if (authenticated) search(pending);
        else status.textContent = message('verifyMessage', 'Continue to verify the email used at checkout.');
      }
    }
  }

  function initializeAll() {
    document.querySelectorAll('[data-order-tracking]').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();

